import React, { useEffect, useRef, useState } from "react";
import { IonLoading } from "@ionic/react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RecaptchaVerifier } from "firebase/auth";
import { auth, isFirebaseConfigured } from "../firebase";
import { confirmPhoneCode, isNative, PhoneSession, startPhoneSignIn } from "../services/authProviders";
import AuthShell, { inputClass, labelClass } from "./AuthShell";
import { friendlyAuthError } from "./Login";

/** Connexion par SMS. Web : reCAPTCHA invisible ; natif : plugin Firebase (Play Integrity / APNs). */
const PhoneLogin: React.FC<{ embedded?: boolean; onDone?: () => void }> = ({ embedded, onDone }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [phone, setPhone] = useState("+41");
  const [code, setCode] = useState("");
  const [session, setSession] = useState<PhoneSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const verifier = useRef<RecaptchaVerifier | null>(null);
  const captchaId = useRef(`recaptcha-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (isNative()) return;
    try {
      verifier.current = new RecaptchaVerifier(auth, captchaId.current, { size: "invisible" });
    } catch (e) {
      verifier.current = null;
    }
    return () => {
      verifier.current?.clear();
      verifier.current = null;
    };
  }, []);

  const send = async () => {
    if (!isFirebaseConfigured) return setError(t("auth.errors.notConfigured"));
    const normalized = phone.replace(/[\s.-]/g, "");
    if (!/^\+\d{8,15}$/.test(normalized)) return setError(t("auth.errors.invalidPhone"));
    setLoading(true);
    try {
      setSession(await startPhoneSignIn(normalized, verifier.current));
    } catch (e: any) {
      setError(friendlyAuthError(e?.code, t));
    } finally {
      setLoading(false);
    }
  };

  const confirm = async () => {
    if (!session || code.trim().length < 6) return setError(t("auth.errors.invalidCode"));
    setLoading(true);
    try {
      await confirmPhoneCode(session, code.trim());
      onDone && onDone();
    } catch (e: any) {
      setError(friendlyAuthError(e?.code, t));
    } finally {
      setLoading(false);
    }
  };

  const form = (
    <div className="space-y-4">
      <IonLoading isOpen={loading} message={t("common.loading")} />
      {!session ? (
        <>
          <div className="space-y-2">
            <label className={labelClass}>
              <i className="fa-solid fa-mobile-screen text-primary-400" />
              {t("auth.phone.number")}
            </label>
            <input type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+41 79 123 45 67" className={inputClass} />
            <p className="text-[11px] text-gray-400 ml-1">{t("auth.phone.hint")}</p>
          </div>
          <div id={captchaId.current} />
          <button type="button" onClick={send} className="btn-primary w-full">
            {t("auth.phone.send")}
          </button>
        </>
      ) : (
        <>
          <div className="space-y-2">
            <label className={labelClass}>
              <i className="fa-solid fa-shield-halved text-primary-400" />
              {t("auth.phone.code")}
            </label>
            <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className={`${inputClass} tracking-[0.4em] text-center text-xl`} />
            <p className="text-[11px] text-gray-400 ml-1">{t("auth.phone.codeHint", { phone })}</p>
          </div>
          <button type="button" onClick={confirm} className="btn-primary w-full">
            {t("auth.phone.confirm")}
          </button>
          <button type="button" onClick={() => setSession(null)} className="w-full text-xs text-gray-400 py-1">
            {t("auth.phone.changeNumber")}
          </button>
        </>
      )}
    </div>
  );

  if (embedded) {
    return (
      <>
        {error && (
          <div className="text-sm text-red-200 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2 mb-3" onClick={() => setError(null)}>
            {error}
          </div>
        )}
        {form}
      </>
    );
  }

  return (
    <AuthShell
      title={t("auth.phone.title")}
      subtitle={t("auth.phone.subtitle")}
      icon="fa-mobile-screen"
      onBack={() => history.push("/login")}
      backLabel={t("common.back")}
      alert={{ open: Boolean(error), header: t("common.error"), message: error || "", onClose: () => setError(null), dismissLabel: t("common.close") }}>
      {form}
    </AuthShell>
  );
};

export default PhoneLogin;
